import { ReactNode } from 'react';
import {
  IconBuildingChurch,
  IconCalendar,
  IconChecklist,
  IconFolders,
  IconShoppingCart,
  IconUsers,
  IconUser,
  IconLayoutDashboard,
  IconListDetails,
  IconListCheck,
  IconUserShield,
  IconComponents,
  IconPalette,
  IconTypography,
  IconForms,
  IconTable,
  IconLanguage,
  IconMoon,
  IconSquare,
  IconDatabase,
  IconSettings,
  IconSparkles,
  IconClick,
  IconArrowsMove,
  IconBriefcase,
  IconAccessible,
  IconBell,
  IconEdit,
  IconLayoutGrid,
  IconApps,
  IconUserCircle,
  IconFolder,
  IconChartBar,
  IconMessage,
  IconReceipt,
  IconHeadset,
  IconLock,
  IconFileText,
  IconServer,
  IconLogin,
  IconUserPlus,
  IconKey,
  IconLockCheck,
  IconDeviceMobile,
  IconDeviceDesktop,
  IconDots,
  IconSend,
  IconChartLine,
  IconGauge,
  IconAlertCircle,
  IconBrain,
  IconChartArea,
  IconChartPie,
  IconTableOptions,
  IconChartDots,
  IconMapPin,
  IconHierarchy,
  IconMessageCircle,
  IconBooks,
  IconBrandFacebook,
  IconBuildingStore,
  IconShoppingBag,
  IconSpeakerphone,
  IconCalendarEvent,
  IconBox,
  IconQrcode,
} from '@tabler/icons-react';

export type DomainKey =
  | 'projects'
  | 'tasks'
  | 'members'
  | 'procurements'
  | 'meetings'
  | 'campus-report'
  | 'demo'
  | 'locations'
  | 'departments'
  | 'feedback'
  | 'resources'
  | 'social-media'
  | 'vendors'
  | 'store'
  | 'announcements'
  | 'bookings'
  | 'qr-codes';

  export interface DomainConfig {
  key: DomainKey;
  label: string;
  description: string;
  icon: ReactNode;
  basePath: string;
  headerActions?: ReactNode;
  menuItems?: DomainMenuItem[];
  menuSections?: DomainMenuSection[];
  // Access control
  requiredRoles?: string[]; // User must have at least one of these roles
  requiredPermissions?: string[]; // User must have at least one of these permissions (rights)
  requireAll?: boolean; // If true, user must have ALL roles/permissions instead of ANY
  strictAccess?: boolean; // If true, Admin users must still have the specific roles (no Admin bypass)
  // Notifications/Badges
  badge?: string | number; // Badge count or label to show on domain card
  badgeColor?: string; // Badge color (default: red for counts, gray for labels)
}

export interface DomainMenuItem {
  label: string;
  path: string;
  icon?: ReactNode;
  requiredRoles?: string[];
  requiredPermissions?: string[];
  requireAll?: boolean;
  strictAccess?: boolean; // If true, Admin users must still have the specific roles (no Admin bypass)
  badge?: string | number;
  badgeColor?: string;
}

export interface DomainMenuSection {
  label: string;
  icon?: ReactNode;
  items: DomainMenuItem[];
  defaultOpen?: boolean;
}

// Global menu sections for sidebar (not tied to any specific domain)
export const GLOBAL_MENU_SECTIONS: DomainMenuSection[] = [
  {
    label: 'App',
    defaultOpen: true,
    items: [
      { label: 'Announcements', path: '/app/announcements', icon: <IconSpeakerphone size={16} />, requiredRoles: ['Announcements Access'], strictAccess: true },
      { label: 'Bookings', path: '/app/bookings', icon: <IconCalendarEvent size={16} />, requiredRoles: ['Staff', 'BookingAdmin', 'Admin'] },
      { label: 'Campus Report', path: '/app/campus-report', icon: <IconBuildingChurch size={16} />, requiredRoles: ['CPC Access'], strictAccess: true },
      { label: 'Departments', path: '/app/departments', icon: <IconHierarchy size={16} />, requiredRoles: ['Departments Access'], strictAccess: true },
      { label: 'Feedback', path: '/app/feedback', icon: <IconMessageCircle size={16} />, requiredRoles: ['Feedback Access'], strictAccess: true, badge: 15 },
      { label: 'Locations', path: '/app/locations', icon: <IconMapPin size={16} />, requiredRoles: ['Locations Access'], strictAccess: true },
      { label: 'Meetings', path: '/app/meetings', icon: <IconCalendar size={16} />, requiredRoles: ['Meetings Access'], strictAccess: true },
      { label: 'Members', path: '/app/members', icon: <IconUsers size={16} />, requiredRoles: ['Members Access', 'Staff'] },
      { label: 'Procurements', path: '/app/procurements', icon: <IconShoppingCart size={16} />, requiredRoles: ['ProcurementsAccess'], strictAccess: true, badge: 5 },
      { label: 'Projects', path: '/app/projects', icon: <IconFolders size={16} />, requiredRoles: ['Projects Access'], strictAccess: true },
      { label: 'QR Codes', path: '/app/qr-codes', icon: <IconQrcode size={16} />, requiredRoles: ['Staff'] },
      { label: 'Resources', path: '/app/resources', icon: <IconBooks size={16} />, requiredRoles: ['Resources Access'], strictAccess: true },
      { label: 'Social Media', path: '/app/social-media', icon: <IconBrandFacebook size={16} />, requiredRoles: ['Social Media Access'], strictAccess: true },
      { label: 'Store', path: '/app/store', icon: <IconShoppingBag size={16} />, requiredRoles: ['Store Access'], strictAccess: true, badge: 28 },
      { label: 'Tasks', path: '/app/tasks', icon: <IconChecklist size={16} />, requiredRoles: ['Tasks Access'], strictAccess: true, badge: 12 },
      { label: 'Vendors', path: '/app/vendors', icon: <IconBuildingStore size={16} />, requiredRoles: ['Vendors Access'], strictAccess: true, badge: 5 },
    ],
  },
  {
    label: 'User',
    defaultOpen: true,
    items: [
      { label: 'Profile', path: '/profile', icon: <IconUser size={16} /> },
      { label: 'Settings', path: '/settings', icon: <IconSettings size={16} /> },
    ],
  },
  {
    label: 'Admin',
    defaultOpen: false,
    items: [
      { label: 'Users', path: '/admin/users', icon: <IconUsers size={16} />, requiredRoles: ['Admin'], strictAccess: true, badge: 3 },
      { label: 'Permissions', path: '/admin/permissions', icon: <IconLock size={16} />, requiredRoles: ['Admin'], strictAccess: true },
      { label: 'Reports', path: '/admin/reports', icon: <IconChartBar size={16} />, requiredRoles: ['Admin'], strictAccess: true },
      { label: 'System Log', path: '/admin/system-log', icon: <IconFileText size={16} />, requiredRoles: ['Admin'], strictAccess: true },
      { label: 'System', path: '/admin/system', icon: <IconServer size={16} />, requiredRoles: ['Admin'], strictAccess: true },
      { label: 'Presence Management', path: '/admin/presence', icon: <IconDeviceDesktop size={16} />, requiredRoles: ['Admin'], strictAccess: true },
      { label: 'Infrastructure', path: '/admin/infrastructure', icon: <IconServer size={16} />, requiredRoles: ['Admin'], strictAccess: true },
      { label: 'Communications', path: '/admin/communications', icon: <IconSend size={16} />, requiredRoles: ['Admin'], strictAccess: true },
      { label: 'File Management', path: '/admin/file-management', icon: <IconFolder size={16} />, requiredRoles: ['Admin'], strictAccess: true },
    ],
  },
];

export const DOMAINS: Record<DomainKey, DomainConfig> = {
  projects: {
    key: 'projects',
    label: 'Projects',
    description: 'Plan and track projects, milestones, and deadlines.',
    icon: <IconFolders size={48} />,
    basePath: '/app/projects',
    requiredRoles: ['Projects Access'],
    strictAccess: true,
    menuItems: [
      { label: 'Overview', path: '/app/projects', icon: <IconLayoutDashboard size={16} /> },
      { label: 'Project list', path: '/app/projects/list', icon: <IconListDetails size={16} /> },
    ],
  },
  tasks: {
    key: 'tasks',
    label: 'Tasks',
    description: 'Manage tasks, assignments, and status.',
    icon: <IconChecklist size={48} />,
    basePath: '/app/tasks',
    requiredRoles: ['Tasks Access'],
    strictAccess: true,
    badge: 12,
    badgeColor: 'red',
    menuItems: [
      { label: 'Overview', path: '/app/tasks', icon: <IconLayoutDashboard size={16} /> },
      { label: 'Board', path: '/app/tasks/board', icon: <IconListCheck size={16} /> },
    ],
  },
  members: {
    key: 'members',
    label: 'HR Management',
    description: 'My profile, leaves, files, and policies — plus HR admin operations for HRAdmin users.',
    icon: <IconUsers size={48} />,
    basePath: '/app/members',
    requiredRoles: ['Members Access', 'Staff', 'HRAdmin', 'Admin'],
    menuItems: [
      { label: 'Overview', path: '/app/members', icon: <IconLayoutDashboard size={16} /> },
      { label: 'Directory', path: '/app/members/list', icon: <IconListDetails size={16} /> },
    ],
  },
  'qr-codes': {
    key: 'qr-codes',
    label: 'QR Codes',
    description: 'Create, manage, and track QR codes with dynamic redirects and scan analytics.',
    icon: <IconQrcode size={48} />,
    basePath: '/app/qr-codes',
    requiredRoles: ['Staff'],
    menuItems: [
      { label: 'Dashboard', path: '/app/qr-codes', icon: <IconLayoutDashboard size={16} /> },
      { label: 'My QR Codes', path: '/app/qr-codes/my-codes', icon: <IconListDetails size={16} /> },
    ],
  },
  procurements: {
    key: 'procurements',
    label: 'Procurements',
    description: 'Handle procurement requests and approvals.',
    icon: <IconShoppingCart size={48} />,
    basePath: '/app/procurements',
    requiredRoles: ['Procurements Access'],
    strictAccess: true,
    badge: 5,
    badgeColor: 'orange',
    menuItems: [
      { label: 'Overview', path: '/app/procurements', icon: <IconLayoutDashboard size={16} /> },
    ],
  },
  meetings: {
    key: 'meetings',
    label: 'Meetings',
    description: 'Schedule and manage meetings.',
    icon: <IconCalendar size={48} />,
    basePath: '/app/meetings',
    requiredRoles: ['Meetings Access'],
    strictAccess: true,
    menuItems: [
      { label: 'Overview', path: '/app/meetings', icon: <IconLayoutDashboard size={16} /> },
    ],
  },
  'campus-report': {
    key: 'campus-report',
    label: 'Campus Report',
    description: 'View reports and metrics for all campuses.',
    icon: <IconBuildingChurch size={48} />,
    basePath: '/app/campus-report',
    requiredRoles: ['CPC Access'],
    strictAccess: true,
    menuItems: [
      { label: 'Dashboard', path: '/app/campus-report', icon: <IconLayoutDashboard size={16} /> },
    ],
  },
  demo: {
    key: 'demo',
    label: 'Demo Components',
    description: 'Explore UI components, themes, and features.',
    icon: <IconComponents size={48} />,
    basePath: '/app/demo',
    requiredRoles: ['Developers'],
    strictAccess: true,
    // Menu is defined in domain/providers/demoMenu.tsx (Menu Domain Contract)
    menuSections: [
  //     {
  //       label: 'Overview',
  //       icon: <IconLayoutDashboard size={14} />,
  //       defaultOpen: true,
  //       items: [
  //         { label: 'Overview', path: '/app/demo', icon: <IconLayoutDashboard size={16} /> },
  //       ],
  //     },
  //     {
  //       label: 'App',
  //       icon: <IconApps size={14} />,
  //       defaultOpen: true,
  //       items: [
  //         { label: 'Dashboard', path: '/app/demo/dashboard', icon: <IconLayoutGrid size={16} /> },
  //       ],
  //     },
  //     {
  //       label: 'Apps',
  //       icon: <IconApps size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'All Apps', path: '/app/demo/apps', icon: <IconLayoutGrid size={16} /> },
  //       ],
  //     },
  //     {
  //       label: 'E-Commerce',
  //       icon: <IconShoppingCart size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Dashboard', path: '/app/demo/apps/ecommerce', icon: <IconLayoutDashboard size={16} /> },
  //         { label: 'Products', path: '/app/demo/apps/ecommerce/products', icon: <IconShoppingCart size={16} />, badge: 24 },
  //         { label: 'Orders', path: '/app/demo/apps/ecommerce/orders', icon: <IconListDetails size={16} />, badge: 156, badgeColor: 'green' },
  //         { label: 'Customers', path: '/app/demo/apps/ecommerce/customers', icon: <IconUsers size={16} />, badge: 89 },
  //       ],
  //     },
  //     {
  //       label: 'Project Management',
  //       icon: <IconFolders size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Dashboard', path: '/app/demo/apps/project-management', icon: <IconLayoutDashboard size={16} /> },
  //         { label: 'View Projects', path: '/app/demo/apps/project-management/projects', icon: <IconFolders size={16} />, badge: 12 },
  //         { label: 'View Board', path: '/app/demo/apps/project-management/board', icon: <IconListCheck size={16} />, badge: 8, badgeColor: 'orange' },
  //         { label: 'View Team', path: '/app/demo/apps/project-management/team', icon: <IconUsers size={16} />, badge: 24 },
  //       ],
  //     },
  //     {
  //       label: 'CRM',
  //       icon: <IconUsers size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Dashboard', path: '/app/demo/apps/crm', icon: <IconLayoutDashboard size={16} /> },
  //         { label: 'Pipeline', path: '/app/demo/apps/crm/pipeline', icon: <IconListCheck size={16} />, badge: '$2.4M', badgeColor: 'green' },
  //         { label: 'Contacts', path: '/app/demo/apps/crm/contacts', icon: <IconUsers size={16} />, badge: 156 },
  //         { label: 'Activities', path: '/app/demo/apps/crm/activities', icon: <IconChecklist size={16} />, badge: 23, badgeColor: 'orange' },
  //       ],
  //     },
  //     {
  //       label: 'HR Portal',
  //       icon: <IconUserCircle size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Dashboard', path: '/app/demo/apps/hr-portal', icon: <IconLayoutDashboard size={16} /> },
  //         { label: 'Directory', path: '/app/demo/apps/hr-portal/directory', icon: <IconUsers size={16} />, badge: 248 },
  //         { label: 'Organization', path: '/app/demo/apps/hr-portal/organization', icon: <IconFolders size={16} />, badge: 12 },
  //         { label: 'Leave Management', path: '/app/demo/apps/hr-portal/leave', icon: <IconCalendar size={16} />, badge: 5, badgeColor: 'red' },
  //       ],
  //     },
  //     {
  //       label: 'File Manager',
  //       icon: <IconFolder size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Dashboard', path: '/app/demo/apps/file-manager', icon: <IconLayoutDashboard size={16} /> },
  //         { label: 'My Files', path: '/app/demo/apps/file-manager/my-files', icon: <IconFolder size={16} />, badge: 128 },
  //         { label: 'Shared', path: '/app/demo/apps/file-manager/shared', icon: <IconUsers size={16} />, badge: 45 },
  //         { label: 'Trash', path: '/app/demo/apps/file-manager/trash', icon: <IconChecklist size={16} />, badge: 8, badgeColor: 'gray' },
  //       ],
  //     },
  //     {
  //       label: 'Analytics',
  //       icon: <IconChartBar size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Dashboard', path: '/app/demo/apps/analytics', icon: <IconLayoutDashboard size={16} /> },
  //         { label: 'Reports', path: '/app/demo/apps/analytics/reports', icon: <IconListDetails size={16} />, badge: 15 },
  //         { label: 'Audience', path: '/app/demo/apps/analytics/audience', icon: <IconUsers size={16} />, badge: '24.5K' },
  //         { label: 'Exports', path: '/app/demo/apps/analytics/exports', icon: <IconDatabase size={16} />, badge: 3, badgeColor: 'cyan' },
  //       ],
  //     },
  //     {
  //       label: 'Chat',
  //       icon: <IconMessage size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Messages', path: '/app/demo/apps/chat', icon: <IconMessage size={16} />, badge: 12, badgeColor: 'red' },
  //         { label: 'Contacts', path: '/app/demo/apps/chat/contacts', icon: <IconUsers size={16} />, badge: 86 },
  //         { label: 'Groups', path: '/app/demo/apps/chat/groups', icon: <IconUsers size={16} />, badge: 8 },
  //         { label: 'Settings', path: '/app/demo/apps/chat/settings', icon: <IconSettings size={16} /> },
  //       ],
  //     },
  //     {
  //       label: 'Calendar',
  //       icon: <IconCalendar size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Calendar', path: '/app/demo/apps/calendar', icon: <IconCalendar size={16} /> },
  //         { label: 'Events', path: '/app/demo/apps/calendar/events', icon: <IconListDetails size={16} />, badge: 28 },
  //         { label: 'Schedule', path: '/app/demo/apps/calendar/schedule', icon: <IconChecklist size={16} />, badge: 5, badgeColor: 'violet' },
  //         { label: 'Meeting Rooms', path: '/app/demo/apps/calendar/rooms', icon: <IconFolders size={16} />, badge: 6 },
  //       ],
  //     },
  //     {
  //       label: 'Invoicing',
  //       icon: <IconReceipt size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Dashboard', path: '/app/demo/apps/invoicing', icon: <IconLayoutDashboard size={16} /> },
  //         { label: 'Invoices', path: '/app/demo/apps/invoicing/invoices', icon: <IconReceipt size={16} />, badge: 42 },
  //         { label: 'Payments', path: '/app/demo/apps/invoicing/payments', icon: <IconShoppingCart size={16} />, badge: 8, badgeColor: 'yellow' },
  //         { label: 'Clients', path: '/app/demo/apps/invoicing/clients', icon: <IconUsers size={16} />, badge: 67 },
  //       ],
  //     },
  //     {
  //       label: 'Helpdesk',
  //       icon: <IconHeadset size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Dashboard', path: '/app/demo/apps/helpdesk', icon: <IconLayoutDashboard size={16} /> },
  //         { label: 'Tickets', path: '/app/demo/apps/helpdesk/tickets', icon: <IconListDetails size={16} />, badge: 23, badgeColor: 'red' },
  //         { label: 'Knowledge Base', path: '/app/demo/apps/helpdesk/knowledge', icon: <IconDatabase size={16} />, badge: 156 },
  //         { label: 'Reports', path: '/app/demo/apps/helpdesk/reports', icon: <IconChartBar size={16} /> },
  //       ],
  //     },
  //     {
  //       label: 'UI Components',
  //       icon: <IconComponents size={14} />,
  //       defaultOpen: true,
  //       items: [
  //         { label: 'Core', path: '/app/demo/ui/core', icon: <IconSquare size={16} /> },
  //         { label: 'Inputs', path: '/app/demo/ui/inputs', icon: <IconForms size={16} /> },
  //         { label: 'Data Display', path: '/app/demo/ui/data-display', icon: <IconDatabase size={16} /> },
  //         { label: 'Feedback', path: '/app/demo/ui/feedback', icon: <IconSparkles size={16} /> },
  //         { label: 'Alerts', path: '/app/demo/ui/alerts', icon: <IconBell size={16} /> },
  //         { label: 'Navigation', path: '/app/demo/ui/navigation', icon: <IconListCheck size={16} /> },
  //         { label: 'Overlays', path: '/app/demo/ui/overlays', icon: <IconClick size={16} /> },
  //         { label: 'Layout', path: '/app/demo/ui/layout', icon: <IconTable size={16} /> },
  //       ],
  //     },
  //     {
  //       label: 'Advanced',
  //       icon: <IconBriefcase size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Calendar', path: '/app/demo/advanced/calendar', icon: <IconCalendar size={16} /> },
  //         { label: 'Workflow', path: '/app/demo/advanced/workflow', icon: <IconChecklist size={16} /> },
  //         { label: 'Editor', path: '/app/demo/advanced/editor', icon: <IconEdit size={16} /> },
  //         { label: 'Data Tables', path: '/app/demo/advanced/data-tables', icon: <IconTable size={16} /> },
  //         { label: 'Form Builder', path: '/app/demo/advanced/form-builder', icon: <IconForms size={16} />, badge: 'New', badgeColor: 'green' },
  //         { label: 'KPI Widgets', path: '/app/demo/advanced/widgets/kpi', icon: <IconChartBar size={16} /> },
  //         { label: 'Line Chart Widgets', path: '/app/demo/advanced/widgets/line-chart', icon: <IconChartLine size={16} /> },
  //         { label: 'Bar Chart Widgets', path: '/app/demo/advanced/widgets/bar-chart', icon: <IconChartBar size={16} /> },
  //         { label: 'Pie Chart Widgets', path: '/app/demo/advanced/widgets/pie-chart', icon: <IconChartPie size={16} /> },
  //         { label: 'Area Chart Widgets', path: '/app/demo/advanced/widgets/area-chart', icon: <IconChartArea size={16} /> },
  //         { label: 'Data Table Widgets', path: '/app/demo/advanced/widgets/data-table', icon: <IconTableOptions size={16} /> },
  //         { label: 'Gauge Widgets', path: '/app/demo/advanced/widgets/gauges', icon: <IconGauge size={16} /> },
  //         { label: 'Heatmap Widgets', path: '/app/demo/advanced/widgets/heatmaps', icon: <IconChartDots size={16} /> },
  //         { label: 'Alert Widgets', path: '/app/demo/advanced/widgets/alerts', icon: <IconAlertCircle size={16} /> },
  //         { label: 'AI Insight Widgets', path: '/app/demo/advanced/widgets/ai-insights', icon: <IconBrain size={16} /> },
  //       ],
  //     },
  //     {
  //       label: 'Animations',
  //       icon: <IconSparkles size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Transitions', path: '/app/demo/animations/transitions', icon: <IconSparkles size={16} /> },
  //         { label: 'Interactive', path: '/app/demo/animations/interactive', icon: <IconClick size={16} /> },
  //         { label: 'Scroll Effects', path: '/app/demo/animations/scroll', icon: <IconArrowsMove size={16} /> },
  //       ],
  //     },
  //     {
  //       label: 'Domain Examples',
  //       icon: <IconFolders size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Projects', path: '/app/demo/domains/projects', icon: <IconFolders size={16} /> },
  //         { label: 'Tasks', path: '/app/demo/domains/tasks', icon: <IconChecklist size={16} /> },
  //         { label: 'Members', path: '/app/demo/domains/members', icon: <IconUsers size={16} /> },
  //       ],
  //     },
  //     {
  //       label: 'Settings & Theming',
  //       icon: <IconSettings size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Color Schemes', path: '/app/demo/settings/colors', icon: <IconPalette size={16} /> },
  //         { label: 'Theme Mode', path: '/app/demo/settings/theme', icon: <IconMoon size={16} /> },
  //         { label: 'Typography', path: '/app/demo/settings/typography', icon: <IconTypography size={16} /> },
  //         { label: 'Translations', path: '/app/demo/settings/translations', icon: <IconLanguage size={16} /> },
  //         { label: 'Accessibility', path: '/app/demo/settings/accessibility', icon: <IconAccessible size={16} /> },
  //       ],
  //     },
  //     {
  //       label: 'Auth Pages',
  //       icon: <IconLock size={14} />,
  //       defaultOpen: false,
  //       items: [
  //         { label: 'Overview', path: '/app/demo/auth-pages', icon: <IconLayoutGrid size={16} /> },
  //         { label: 'Login', path: '/app/demo/auth-pages/login', icon: <IconLogin size={16} />, badge: 17 },
  //         { label: 'Registration', path: '/app/demo/auth-pages/register', icon: <IconUserPlus size={16} />, badge: 17 },
  //         { label: 'Forgot Password', path: '/app/demo/auth-pages/forgot-password', icon: <IconKey size={16} />, badge: 17 },
  //         { label: 'Reset Password', path: '/app/demo/auth-pages/reset-password', icon: <IconLockCheck size={16} />, badge: 17 },
  //         { label: 'OTP / 2FA', path: '/app/demo/auth-pages/otp', icon: <IconDeviceMobile size={16} />, badge: 17 },
  //         { label: 'Other', path: '/app/demo/auth-pages/other', icon: <IconDots size={16} />, badge: 5 },
  //       ],
  //     },
    ],
  },
  locations: {
    key: 'locations',
    label: 'Locations',
    description: 'Manage campuses, buildings, and geographic areas.',
    icon: <IconMapPin size={48} />,
    basePath: '/app/locations',
    requiredRoles: ['Locations Access'],
    strictAccess: true,
    menuItems: [],
  },
  departments: {
    key: 'departments',
    label: 'Departments',
    description: 'Organize and manage church departments.',
    icon: <IconHierarchy size={48} />,
    basePath: '/app/departments',
    requiredRoles: ['Departments Access'],
    strictAccess: true,
    menuItems: [],
  },
  feedback: {
    key: 'feedback',
    label: 'Feedback',
    description: 'Collect and manage member feedback.',
    icon: <IconMessageCircle size={48} />,
    basePath: '/app/feedback',
    requiredRoles: ['Feedback Access'],
    strictAccess: true,
    badge: 15,
    badgeColor: 'blue',
    menuItems: [],
  },
  resources: {
    key: 'resources',
    label: 'Resources',
    description: 'Library of books, videos, audio, and documents.',
    icon: <IconBooks size={48} />,
    basePath: '/app/resources',
    requiredRoles: ['Resources Access'],
    strictAccess: true,
    menuItems: [],
  },
  'social-media': {
    key: 'social-media',
    label: 'Social Media',
    description: 'Manage social media presence and engagement.',
    icon: <IconBrandFacebook size={48} />,
    basePath: '/app/social-media',
    requiredRoles: ['Social Media Access'],
    strictAccess: true,
    menuItems: [],
  },
  vendors: {
    key: 'vendors',
    label: 'Vendors',
    description: 'Manage vendor relationships and contracts.',
    icon: <IconBuildingStore size={48} />,
    basePath: '/app/vendors',
    requiredRoles: ['Vendors Access'],
    strictAccess: true,
    badge: 5,
    badgeColor: 'orange',
    menuItems: [],
  },
  announcements: {
    key: 'announcements',
    label: 'Announcements',
    description: 'Create and manage announcements across locations.',
    icon: <IconSpeakerphone size={48} />,
    basePath: '/app/announcements',
    requiredRoles: ['Announcements Access'],
    strictAccess: true,
    menuItems: [],
  },
  store: {
    key: 'store',
    label: 'Store',
    description: 'Products, orders, payments, and customers.',
    icon: <IconShoppingBag size={48} />,
    basePath: '/app/store',
    requiredRoles: ['Store Access'],
    strictAccess: true,
    badge: 28,
    badgeColor: 'green',
    menuItems: [],
  },
  bookings: {
    key: 'bookings',
    label: 'Bookings',
    description: 'Book and manage physical resources — cars, phones, equipment, and rooms.',
    icon: <IconCalendarEvent size={48} />,
    basePath: '/app/bookings',
    // Staff reach the self-service portal; BookingAdmin/Admin additionally
    // unlock the admin sections. Listing all three here ensures a BookingAdmin
    // who isn't also flagged Staff is never blocked at the domain gate —
    // item-level visibility (see domain/providers/bookingsMenu.tsx) decides
    // what each role actually sees inside.
    requiredRoles: ['Staff', 'BookingAdmin', 'Admin'],
    strictAccess: false,
    menuItems: [
      { label: 'Dashboard', path: '/app/bookings', icon: <IconLayoutDashboard size={16} /> },
      { label: 'My Bookings', path: '/app/bookings/my-bookings', icon: <IconListDetails size={16} /> },
      { label: 'My Licence', path: '/app/bookings/my-licences', icon: <IconListDetails size={16} /> },
      { label: 'Calendar', path: '/app/bookings/calendar', icon: <IconCalendar size={16} /> },
      { label: 'Overview', path: '/app/bookings/admin/overview', icon: <IconLayoutDashboard size={16} />, requiredRoles: ['BookingAdmin', 'Admin'] },
      { label: 'All Bookings', path: '/app/bookings/all', icon: <IconCalendarEvent size={16} />, requiredRoles: ['BookingAdmin', 'Admin'] },
      { label: 'Resources', path: '/app/bookings/resources', icon: <IconBox size={16} />, requiredRoles: ['BookingAdmin', 'Admin'] },
      { label: 'Settings', path: '/app/bookings/settings', icon: <IconSettings size={16} />, requiredRoles: ['BookingAdmin', 'Admin'] },
      { label: 'Reports', path: '/app/bookings/reports', icon: <IconChartBar size={16} />, requiredRoles: ['BookingAdmin', 'Admin'] },
    ],
  },
};
